import React, { useState, useEffect, useRef } from 'react'
import styles from './CaseStudySection.module.scss'

import sendImage from '@assets/img/send.png'

import { WithI18nProps } from '@/types/i18n'
import { createMerchantI18n, getLocaleStyleClass } from '@/utils/i18n'
import { withTranslator } from '@common/hoc/translatorHoc'
import lottie, { AnimationItem } from 'lottie-web'
import bottomButtonAnimation from '@common/json/bottomButton.json'


const CaseStudySection: React.FC<WithI18nProps & { onJoin: () => void }> = ({ i18n, onJoin }) => {
  const t = React.useMemo(() => createMerchantI18n(i18n), [i18n])
  const [isFixed, setIsFixed] = useState(false)
  const btnWrapperRef = useRef<HTMLDivElement>(null)
  const btnInitialTopRef = useRef<number>(0)
  const lottieContainerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<AnimationItem | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollAnimationRef = useRef<number | null>(null)
  const isPausedRef = useRef(false)

  const caseList = [
    {
      title: t('case1Country', "香港") + ' | ' + t('case1StoreName', "红棉"),
      desc: t('case1StoreDesc', "借力单店热度，通过代金券及优惠套餐交易带动集团旗下门店实现生意成长"),
      bg: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/3c522f7bda1a4f54b768ba78bc3423172368384.png',
      logo: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/39ca05572b64a55a0bde37cfc171ee38100468.png',
      stats: [
        { num: t('case1Item1Value', "500,000+"), label: t('case1Item1Title', "月均GTV") },
        { num: t('case1Item2Value', "2,000+"), label: t('case1Item2Title', "月均套餐销量") },
        { num: t('case1Item3Value', "600%+"), label: t('case1Item3Title', "门店流量提升") },
      ]
    },
    {
      title: t('case2Country', "大阪") + ' | ' + t('case2StoreName', "黑毛和牛烧肉涮锅寿喜烧 善"),
      desc: t('case2StoreDesc', "优惠套餐紧扣招牌菜，带动销量、好评等线上数据快速积累，助力门店跃升地区美食热门榜TOP1"),
      bg: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/f0bb6cd2aaf97b12e5317d6e468918492848810.png',
      logo: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/c2e87d330ca71b1d38100f28ba97132a591197.png',
      stats: [
        { num: t('case2Item1Value', "百万级"), label: t('case2Item1Title', "累计月均GTV") },
        { num: t('case2Item2Value', "30,000+"), label: t('case2Item2Title', "累计套餐销量") },
        { num: t('case2Item3Value', "90%+"), label: t('case2Item3Title', "累计好评率") },
      ]
    },
    {
      title: t('case3Country', "新加坡") + ' | ' + t('case3StoreName', "跳舞螃蟹"),
      desc: t('case3StoreDesc', "门槛越高、折扣越优的阶梯式代金券设计，兼顾不同消费能力客群，显著提升门店客单价"),
      bg: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/4c8a40e798af401503d10d3acccb57246539302.png',
      logo: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/a6555bdfe6026a647be2c5fba4264c13466963.png',
      
      stats: [
        { num: t('case3Item1Value', "900+RMB"), label: t('case3Item1Title', "套餐客单价") },
        { num: t('case3Item2Value', "97%+"), label: t('case3Item2Title', "下单转化率") },
        { num: t('case3Item3Value', "5,000+"), label: t('case3Item3Title', "套餐支付用户") },
      ]
    },
    {
      title: t('case4Country', "曼谷") + ' | ' + t('case4StoreName', "Erawan Tea Room"),
      desc: t('case4StoreDesc', "多种代金券选择，适配不同客群；优惠套餐配置合理、种类丰富，覆盖多种消费需求"),
      bg: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/b1b89cd6679bd48e576d833b8a878fed2209327.png',
      logo: 'https://s3plus-shon.meituan.net/overseas-merchant/merchant-home/b61ccfff19d27427c6cd70751b9ac6aa363414.png',
      stats: [
        { num: t('case4Item1Value', "90%+"), label: t('case4Item1Title', "交易核销率") },
        { num: t('case4Item2Value', "TOP2"), label: t('case4Item2Title', "美食热门榜") },
        { num: t('case4Item3Value', "前10%"), label: t('case4Item3Title', "东南亚菜排名") },
      ]
    },
  ]

  // 无缝滚动动画
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // 计算单组卡片的总宽度：4个卡片 * (406px + 30px gap) = 1744px
    const singleSetWidth = 1744
    
    // 初始位置设置为第二组的起始位置，这样前后都有缓冲
    let scrollPosition = singleSetWidth
    
    const scrollSpeed = 0.6 // 每帧移动的像素数，调整这个值可以改变滚动速度

    const animate = () => {
      if (isPausedRef.current) {
        scrollAnimationRef.current = requestAnimationFrame(animate)
        return
      }

      scrollPosition += scrollSpeed

      // 当滚动到第三组的开始位置（即第二组结束）时，重置回第一组的结束位置
      // 因为第一组和第二组内容完全相同，这个跳转在视觉上是无缝的
      if (scrollPosition >= singleSetWidth * 2) {
        scrollPosition = singleSetWidth
      }

      scrollContainer.style.transform = `translateX(-${scrollPosition}px)`
      scrollAnimationRef.current = requestAnimationFrame(animate)
    }

    // 设置初始位置
    scrollContainer.style.transform = `translateX(-${scrollPosition}px)`
    scrollAnimationRef.current = requestAnimationFrame(animate)

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current)
      }
    }
  }, [])

  const getCurrentLanguage = () => {
    if (!i18n) return 'zh-CN';
    return i18n.getLocale() || 'zh-CN';
  };

  // 初始化 Lottie 动画
  useEffect(() => {
    if (lottieContainerRef.current) {
      animationRef.current = lottie.loadAnimation({
        container: lottieContainerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: bottomButtonAnimation
      })
    }

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy()
      }
    }
  }, [])

  useEffect(() => {
    // 延迟计算按钮初始位置，确保 Lottie 动画已完全渲染
    const calculateInitialPosition = () => {
      if (btnWrapperRef.current) {
        const rect = btnWrapperRef.current.getBoundingClientRect()
        const btnHeight = rect.height
        btnInitialTopRef.current = rect.top + window.scrollY
        console.log('按钮初始位置（距离文档顶部）:', btnInitialTopRef.current)
        console.log('按钮实际高度:', btnHeight)
      }
    }

    // 等待 Lottie 动画渲染完成后再计算，增加延迟时间
    const timer = setTimeout(calculateInitialPosition, 500)

    const handleScroll = () => {
      const scrollY = window.scrollY
      const btnInitialTop = btnInitialTopRef.current
      const windowHeight = window.innerHeight

      if (!btnWrapperRef.current || btnInitialTop === 0) return

      // 获取按钮实际高度
      const btnHeight = btnWrapperRef.current.getBoundingClientRect().height
      // 按钮固定在底部时，距离底部约 20px
      const fixedBottomOffset = 20

      // 计算临界点：当视口底部到达按钮底部位置时
      // 按钮底部位置 = btnInitialTop + btnHeight
      // 临界点：scrollY + windowHeight = btnInitialTop + btnHeight + fixedBottomOffset
      // 即：scrollY = btnInitialTop + btnHeight + fixedBottomOffset - windowHeight
      const threshold = btnInitialTop + btnHeight + fixedBottomOffset - windowHeight

      // 当滚动超过 500px 且未到达临界点时，固定按钮
      if (scrollY > 500 && scrollY < threshold) {
        setIsFixed(true)
      } else {
        setIsFixed(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    // 延迟首次调用，确保计算已完成
    const scrollTimer = setTimeout(handleScroll, 600)

    // 清理事件监听器和定时器
    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(timer)
      clearTimeout(scrollTimer)
    }
  }, [])


  const getCurrentLanguageClass = (style: string): string => {
    return getLocaleStyleClass(getCurrentLanguage(), style)
  }




  return (
    <section className={`${styles.section}`}>
      <div className={styles.contentWrapper}>
        <div className={`${styles.sectionHeader} ${styles[getCurrentLanguageClass('sectionHeader')]}`}>
          {t('andThem', "和TA们一样")},{t('implementTrafficGrowth', " 实现流量快速增长")}
        </div>

        <div 
          className={styles.casesWrapper}
          onMouseEnter={() => { isPausedRef.current = true }}
          onMouseLeave={() => { isPausedRef.current = false }}
        >
          <div ref={scrollContainerRef} className={styles.casesGrid}>
            {/* 渲染三遍实现无缝循环，提供足够的缓冲避免视觉跳跃 */}
            {[...caseList, ...caseList, ...caseList].map((c, i) => (
              <div key={i} className={`${styles.caseCard}`}>
                {/* Background Image */}
                <img src={c.bg} alt={c.title} className={styles.caseImg} />

                {/* Center Floating Logo */}
                <div className={styles.logoWrapper}>
                  <img src={c.logo} alt="Logo" />
                </div>

                <div className={styles.caseBody}>
                  <h4 className={`${styles.title} ${ styles[getCurrentLanguageClass('title')]}`}>{c.title}</h4>
                  <div className={`${styles.desc} ${styles[getCurrentLanguageClass('desc')]}`}>{c.desc}</div>
                  <div className={`${styles.stats} ${styles[getCurrentLanguageClass('stats')]}`}>
                    {c.stats.map((stat, idx) => (
                      <div key={idx} className={styles.statItem}> 
                        <div className={`${styles.num} ${ styles[getCurrentLanguageClass('num')]}`  }>{stat.num}</div>
                        <div className={`${styles.label} ${ styles[getCurrentLanguageClass('label')]}`}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 加入我们按钮 */}
        <div className={styles.joinBtnWrapper}>
          <div ref={btnWrapperRef} className={`${styles.joinBtn} ${isFixed ? styles.fixedBtn : ''}`} onClick={onJoin}>
            <div ref={lottieContainerRef} className={styles.lottieBackground} />
            <span className={`${styles.buttonContent} ${styles[getCurrentLanguageClass('buttonContent')]}`}>
              {t('wantToJoin', "我要入驻")}
              <img src={sendImage} alt="send" className={styles.sendImg} />
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

export default withTranslator(CaseStudySection)
